import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  Database,
  FileClock,
  Pencil,
} from "lucide-react";
import { errMsg } from "@shared/api/errors";
import { CalidadControlDocumentalPanel } from "../../../standards/iso-9001-14001/components/control-modificaciones/CalidadControlDocumentalPanel";
import { Iso900114001ConfirmDialog } from "../../../standards/iso-9001-14001/components/shared/Iso900114001ConfirmDialog";
import { Iso900114001IconButton } from "../../../standards/iso-9001-14001/components/shared/Iso900114001IconButton";
import { Iso900114001OverviewCard } from "../../../standards/iso-9001-14001/components/shared/Iso900114001OverviewCard";
import { Iso900114001SectionHeader } from "../../../standards/iso-9001-14001/components/shared/Iso900114001SectionHeader";
import { Iso900114001Tabs } from "../../../standards/iso-9001-14001/components/shared/Iso900114001Tabs";
import { Iso2700127701ControlDocumentalPanel } from "../../../standards/iso-27001-27701/components/control-modificaciones/Iso2700127701ControlDocumentalPanel";
import {
  Iso2700127701ConfirmDialog,
  Iso2700127701IconButton,
  Iso2700127701OverviewCard,
  Iso2700127701SectionHeader,
  Iso2700127701Tabs,
} from "../../../standards/iso-27001-27701/components/shared";
import { indicadoresApi } from "../api/indicadores";
import type {
  IndicadorDataPayload,
  IndicadorDataRow,
  IndicadorDetail,
  IndicadorHubItem,
  IndicadorMetadataPayload,
  IndicadoresStandard,
} from "../types/indicadores";
import { IndicadorDataFormModal } from "./IndicadorDataFormModal";
import { IndicadorMetadataModal } from "./IndicadorMetadataModal";
import { IndicadorRecordsPanel } from "./IndicadorRecordsPanel";
import "../styles/indicadores.css";

type Props = {
  standard: IndicadoresStandard;
  onBack: () => void;
};

type Tab = "reference" | "tracking" | "control";
type RecordFormState =
  | { kind: "none" }
  | { kind: "reference" | "tracking"; row: IndicadorDataRow | null };
type DeleteState =
  | { kind: "none" }
  | { kind: "reference" | "tracking"; row: IndicadorDataRow };

const DEFAULT_INDICATOR_HUB_ITEMS: IndicadorHubItem[] = [
  createIndicatorPlaceholder(1, "numero-clientes", "Número de clientes", true),
  createIndicatorPlaceholder(2, "ofertas-lanzadas", "Ofertas lanzadas", true),
  createIndicatorPlaceholder(3, "ampliacion-servicios-otr", "Ampliación de servicios OTR", true),
  createIndicatorPlaceholder(4, "consumo-papel", "Consumo de papel", false),
  createIndicatorPlaceholder(5, "consumo-electrico", "Consumo eléctrico", true),
  createIndicatorPlaceholder(6, "toner", "Tóner", false),
  createIndicatorPlaceholder(7, "proveedores-directos", "Proveedores directos", false),
  createIndicatorPlaceholder(8, "documentados-diarios", "Documentados diarios", false),
  createIndicatorPlaceholder(9, "asistencia-eventos", "Asistencia a eventos", false),
];

export function IndicadoresSection({ standard, onBack }: Props) {
  const [hubItems, setHubItems] = useState<IndicadorHubItem[]>(DEFAULT_INDICATOR_HUB_ITEMS);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [detail, setDetail] = useState<IndicadorDetail | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("reference");
  const [hubLoading, setHubLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [metadataSubmitting, setMetadataSubmitting] = useState(false);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [recordForm, setRecordForm] = useState<RecordFormState>({ kind: "none" });
  const [recordSubmitting, setRecordSubmitting] = useState(false);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [deleteState, setDeleteState] = useState<DeleteState>({ kind: "none" });
  const [deleting, setDeleting] = useState(false);

  const loadHub = useCallback(async (refresh = false) => {
    refresh ? setRefreshing(true) : setHubLoading(true);
    setError(null);

    try {
      setHubItems(await indicadoresApi.list());
    } catch (requestError) {
      setError(errMsg(requestError, "No se pudieron cargar los indicadores."));
    } finally {
      setHubLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadDetail = useCallback(async (slug: string, refresh = false) => {
    refresh ? setRefreshing(true) : setDetailLoading(true);
    setError(null);

    try {
      setDetail(await indicadoresApi.get(slug));
    } catch (requestError) {
      setError(errMsg(requestError, "No se pudo cargar el indicador."));
    } finally {
      setDetailLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadHub();
  }, [loadHub]);

  useEffect(() => {
    if (selectedSlug) {
      void loadDetail(selectedSlug);
    }
  }, [loadDetail, selectedSlug]);

  const openIndicator = (slug: string) => {
    setActiveTab("reference");
    setDetail(null);
    setDetailLoading(true);
    setSelectedSlug(slug);
  };

  const back = () => {
    if (selectedSlug) {
      setSelectedSlug(null);
      setDetail(null);
      setActiveTab("reference");
      void loadHub(true);
      return;
    }

    onBack();
  };

  const refresh = () => {
    if (selectedSlug) {
      void loadDetail(selectedSlug, true);
      return;
    }

    void loadHub(true);
  };

  const submitMetadata = async (payload: IndicadorMetadataPayload) => {
    if (!selectedSlug) return;

    setMetadataSubmitting(true);
    setMetadataError(null);

    try {
      await indicadoresApi.updateMetadata(selectedSlug, payload);
      setMetadataOpen(false);
      await loadDetail(selectedSlug, true);
    } catch (requestError) {
      setMetadataError(errMsg(requestError, "No se pudo actualizar el indicador."));
    } finally {
      setMetadataSubmitting(false);
    }
  };

  const submitRecord = async (payload: IndicadorDataPayload) => {
    if (!selectedSlug || recordForm.kind === "none") return;

    setRecordSubmitting(true);
    setRecordError(null);

    try {
      if (recordForm.kind === "reference") {
        if (recordForm.row) {
          await indicadoresApi.updateReference(selectedSlug, recordForm.row.id, payload);
        } else {
          await indicadoresApi.createReference(selectedSlug, payload);
        }
      } else if (recordForm.row) {
        await indicadoresApi.updateData(selectedSlug, recordForm.row.id, payload);
      } else {
        await indicadoresApi.createData(selectedSlug, payload);
      }

      setRecordForm({ kind: "none" });
      await loadDetail(selectedSlug, true);
    } catch (requestError) {
      setRecordError(errMsg(requestError, "No se pudieron guardar los datos."));
    } finally {
      setRecordSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedSlug || deleteState.kind === "none") return;

    setDeleting(true);
    setError(null);

    try {
      if (deleteState.kind === "reference") {
        await indicadoresApi.deleteReference(selectedSlug, deleteState.row.id);
      } else {
        await indicadoresApi.deleteData(selectedSlug, deleteState.row.id);
      }

      setDeleteState({ kind: "none" });
      await loadDetail(selectedSlug, true);
    } catch (requestError) {
      setError(errMsg(requestError, "No se pudo eliminar el registro."));
    } finally {
      setDeleting(false);
    }
  };

  const openRecordForm = (
    kind: "reference" | "tracking",
    row: IndicadorDataRow | null,
  ) => {
    setRecordError(null);
    setRecordForm({ kind, row });
  };

  const selectedHubItem = useMemo(
    () => hubItems.find((item) => item.slug === selectedSlug) ?? null,
    [hubItems, selectedSlug],
  );
  const selectedIndicator = detail?.data ?? selectedHubItem;
  const title = selectedIndicator?.nombre ?? "Indicadores";
  const description = selectedIndicator
    ? `Indicador nº ${selectedIndicator.numero}. Consulta sus valores de referencia, seguimiento, gráficos y Control documental.`
    : "Consulta los nueve indicadores compartidos por ISO 9001-14001 e ISO 27001-27701.";
  const formConfig = recordForm.kind === "reference"
    ? detail?.config.reference ?? null
    : recordForm.kind === "tracking"
      ? detail?.config ?? null
      : null;
  const summary = {
    reference: detail?.summary.total_valores_referencia
      ?? selectedHubItem?.total_valores_referencia
      ?? 0,
    tracking: detail?.summary.total_seguimiento
      ?? selectedHubItem?.total_seguimiento
      ?? 0,
    control: detail?.summary.total_control_documental
      ?? selectedHubItem?.total_control_documental
      ?? 0,
  };
  const isInitialDetailLoading = Boolean(selectedSlug && detailLoading && !detail);
  const tabs = selectedSlug
    ? [
        {
          id: "reference" as const,
          label: "Valores Objetivo Alerta / Referencia",
          count: summary.reference,
        },
        {
          id: "tracking" as const,
          label: "Seguimiento de indicadores",
          count: summary.tracking,
        },
        {
          id: "control" as const,
          label: "Control documental",
          count: summary.control,
        },
      ]
    : [];

  return (
    <section className={`indicadores-section indicadores-${standard}`}>
      {standard === "iso-9001-14001" ? (
        <Iso900114001SectionHeader
          eyebrow="Sección compartida"
          title={title}
          description={description}
          onBack={back}
          onRefresh={refresh}
          refreshing={refreshing}
        />
      ) : (
        <Iso2700127701SectionHeader
          eyebrow="Sección compartida"
          title={title}
          description={description}
          onBack={back}
          onRefresh={refresh}
          refreshing={refreshing}
        />
      )}

      {error ? <div className="indicadores-inline-error">{error}</div> : null}

      {!selectedSlug ? (
        <IndicadoresHub items={hubItems} loading={hubLoading} onOpen={openIndicator} />
      ) : null}

      {selectedSlug ? (
        <>
          {detail ? (
            <MetadataPanel
              standard={standard}
              detail={detail}
              onEdit={() => {
                setMetadataError(null);
                setMetadataOpen(true);
              }}
            />
          ) : (
            <MetadataLoadingPanel
              standard={standard}
              name={selectedHubItem?.nombre ?? "Indicador"}
              loading={isInitialDetailLoading}
            />
          )}

          {standard === "iso-9001-14001" ? (
            <div className="iso9001-overview-grid">
              <Iso900114001OverviewCard
                icon={Database}
                label="Valores de referencia"
                value={summary.reference}
              />
              <Iso900114001OverviewCard
                icon={CalendarDays}
                label="Seguimiento"
                value={summary.tracking}
              />
              <Iso900114001OverviewCard
                icon={FileClock}
                label="Control documental"
                value={summary.control}
              />
            </div>
          ) : (
            <div className="iso27001-overview-grid">
              <Iso2700127701OverviewCard
                icon={Database}
                label="Valores de referencia"
                value={summary.reference}
              />
              <Iso2700127701OverviewCard
                icon={CalendarDays}
                label="Seguimiento"
                value={summary.tracking}
              />
              <Iso2700127701OverviewCard
                icon={FileClock}
                label="Control documental"
                value={summary.control}
              />
            </div>
          )}

          {standard === "iso-9001-14001" ? (
            <Iso900114001Tabs
              tabs={tabs}
              activeTab={activeTab}
              onChange={setActiveTab}
              ariaLabel="Contenido del indicador"
            />
          ) : (
            <Iso2700127701Tabs
              tabs={tabs}
              activeTab={activeTab}
              onChange={setActiveTab}
              ariaLabel="Contenido del indicador"
            />
          )}

          {activeTab === "reference" ? (
            <IndicadorRecordsPanel
              standard={standard}
              indicator={detail?.data ?? selectedHubItem}
              config={detail?.config.reference ?? null}
              rows={detail?.reference_rows ?? []}
              loading={isInitialDetailLoading}
              eyebrow="Objetivos, alertas y referencias"
              title="Valores Objetivo Alerta / Referencia"
              createLabel="Nuevo valor"
              emptyText="No hay valores de referencia para los filtros seleccionados."
              onCreate={detail ? () => openRecordForm("reference", null) : () => undefined}
              onEdit={(row) => openRecordForm("reference", row)}
              onDelete={(row) => setDeleteState({ kind: "reference", row })}
            />
          ) : null}

          {activeTab === "tracking" ? (
            <IndicadorRecordsPanel
              standard={standard}
              indicator={detail?.data ?? selectedHubItem}
              config={detail?.config ?? null}
              rows={detail?.rows ?? []}
              loading={isInitialDetailLoading}
              eyebrow="Datos históricos"
              title="Seguimiento de indicadores"
              createLabel="Nuevo registro"
              emptyText="No hay registros de seguimiento para los filtros seleccionados."
              showCharts={detail?.config.chart ?? selectedHubItem?.tiene_graficos ?? false}
              onCreate={detail ? () => openRecordForm("tracking", null) : () => undefined}
              onEdit={(row) => openRecordForm("tracking", row)}
              onDelete={(row) => setDeleteState({ kind: "tracking", row })}
            />
          ) : null}

          {activeTab === "control" ? (
            standard === "iso-9001-14001" ? (
              <CalidadControlDocumentalPanel
                rows={detail?.control_documental ?? []}
                loading={isInitialDetailLoading}
                error={null}
                baseEndpoint={`/certificaciones/indicadores/${selectedSlug}/control-documental`}
                onReload={() => loadDetail(selectedSlug, true)}
              />
            ) : (
              <Iso2700127701ControlDocumentalPanel
                rows={detail?.control_documental ?? []}
                loading={isInitialDetailLoading}
                error={null}
                baseEndpoint={`/certificaciones/indicadores/${selectedSlug}/control-documental`}
                onReload={() => loadDetail(selectedSlug, true)}
              />
            )
          ) : null}
        </>
      ) : null}

      <IndicadorMetadataModal
        standard={standard}
        open={metadataOpen}
        indicator={detail?.data ?? null}
        submitting={metadataSubmitting}
        error={metadataError}
        onClose={() => setMetadataOpen(false)}
        onSubmit={(payload) => void submitMetadata(payload)}
      />

      <IndicadorDataFormModal
        standard={standard}
        open={recordForm.kind !== "none"}
        row={recordForm.kind !== "none" ? recordForm.row : null}
        config={formConfig}
        newTitle={recordForm.kind === "reference" ? "Añadir valor de referencia" : "Añadir seguimiento"}
        editTitle={recordForm.kind === "reference" ? "Editar valor de referencia" : "Editar seguimiento"}
        submitting={recordSubmitting}
        error={recordError}
        onClose={() => setRecordForm({ kind: "none" })}
        onSubmit={(payload) => void submitRecord(payload)}
      />

      {standard === "iso-9001-14001" ? (
        <Iso900114001ConfirmDialog
          open={deleteState.kind !== "none"}
          title={deleteTitle(deleteState)}
          description={deleteDescription(deleteState)}
          confirming={deleting}
          onClose={() => setDeleteState({ kind: "none" })}
          onConfirm={() => void confirmDelete()}
        />
      ) : (
        <Iso2700127701ConfirmDialog
          open={deleteState.kind !== "none"}
          title={deleteTitle(deleteState)}
          description={deleteDescription(deleteState)}
          confirming={deleting}
          onClose={() => setDeleteState({ kind: "none" })}
          onConfirm={() => void confirmDelete()}
        />
      )}
    </section>
  );
}

function IndicadoresHub({
  items,
  loading,
  onOpen,
}: {
  items: IndicadorHubItem[];
  loading: boolean;
  onOpen: (slug: string) => void;
}) {
  return (
    <section className="calidad-hub-grid indicadores-hub-grid">
      {items.map((indicator) => (
        <button
          type="button"
          key={indicator.id}
          className="calidad-hub-card"
          onClick={() => onOpen(indicator.slug)}
        >
          <span className="calidad-hub-card-icon indicadores-hub-number" aria-hidden="true">
            {indicator.numero}
          </span>

          <div className="calidad-hub-card-body">
            <div className="calidad-hub-card-eyebrow">Indicador {indicator.numero}</div>
            <h2 className="calidad-hub-card-title">{indicator.nombre}</h2>
            <p className="calidad-hub-card-copy indicadores-hub-count">
              <BarChart3 size={15} />
              {loading ? "Cargando registros…" : `${indicator.total_registros} registros`}
            </p>
          </div>

          <span className="calidad-hub-card-action">
            Abrir
            <ArrowRight size={16} />
          </span>
        </button>
      ))}
    </section>
  );
}

function createIndicatorPlaceholder(
  numero: number,
  slug: string,
  nombre: string,
  tieneGraficos: boolean,
): IndicadorHubItem {
  return {
    id: numero,
    numero,
    slug,
    nombre,
    descripcion: null,
    criterio_1: null,
    criterio_2: null,
    usuario_modificacion_id: null,
    usuario_modificacion_nombre: null,
    created_at: null,
    updated_at: null,
    total_registros: 0,
    total_seguimiento: 0,
    total_valores_referencia: 0,
    total_control_documental: 0,
    tiene_graficos: tieneGraficos,
  };
}

function MetadataPanel({
  standard,
  detail,
  onEdit,
}: {
  standard: IndicadoresStandard;
  detail: IndicadorDetail;
  onEdit: () => void;
}) {
  const panelClass = standard === "iso-9001-14001"
    ? "iso9001-crud-card indicadores-metadata-panel"
    : "iso27001-table-card indicadores-metadata-panel";

  return (
    <section className={panelClass}>
      <div className="indicadores-metadata-heading">
        <div>
          <div className={standard === "iso-9001-14001" ? "iso9001-section-eyebrow" : "iso27001-table-eyebrow"}>
            Información general
          </div>
          <h3>{detail.data.nombre}</h3>
        </div>

        {standard === "iso-9001-14001" ? (
          <Iso900114001IconButton
            label="Editar indicador"
            icon={<Pencil size={16} />}
            onClick={onEdit}
          />
        ) : (
          <Iso2700127701IconButton
            label="Editar indicador"
            icon={<Pencil size={16} />}
            onClick={onEdit}
          />
        )}
      </div>

      <div className="indicadores-metadata-grid">
        <MetadataBlock label="Descripción" value={detail.data.descripcion} />
        <MetadataBlock label="Criterio 1" value={detail.data.criterio_1} />
        <MetadataBlock label="Criterio 2" value={detail.data.criterio_2} />
      </div>
    </section>
  );
}

function MetadataLoadingPanel({
  standard,
  name,
  loading,
}: {
  standard: IndicadoresStandard;
  name: string;
  loading: boolean;
}) {
  const panelClass = standard === "iso-9001-14001"
    ? "iso9001-crud-card indicadores-metadata-panel"
    : "iso27001-table-card indicadores-metadata-panel";

  return (
    <section className={panelClass} aria-busy={loading}>
      <div className="indicadores-metadata-heading">
        <div>
          <div className={standard === "iso-9001-14001" ? "iso9001-section-eyebrow" : "iso27001-table-eyebrow"}>
            Información general
          </div>
          <h3>{name}</h3>
        </div>
        <span className="indicadores-skeleton indicadores-skeleton-button" aria-hidden="true" />
      </div>

      <div className="indicadores-metadata-grid">
        {["Descripción", "Criterio 1", "Criterio 2"].map((label) => (
          <article key={label}>
            <span>{label}</span>
            <div className="indicadores-skeleton indicadores-skeleton-line" aria-hidden="true" />
            <div className="indicadores-skeleton indicadores-skeleton-line indicadores-skeleton-line-short" aria-hidden="true" />
          </article>
        ))}
      </div>
    </section>
  );
}

function MetadataBlock({ label, value }: { label: string; value: string | null }) {
  return (
    <article>
      <span>{label}</span>
      <p>{value?.trim() || "Sin información"}</p>
    </article>
  );
}

function deleteTitle(state: DeleteState): string {
  if (state.kind === "reference") return "Eliminar valor de referencia";
  return "Eliminar seguimiento";
}

function deleteDescription(state: DeleteState): string {
  if (state.kind === "none") return "";
  if (state.kind === "reference") {
    return "Se eliminará definitivamente este valor de Objetivo Alerta / Referencia.";
  }

  return "Se eliminará definitivamente este registro de seguimiento del indicador.";
}
